/**
 * Error code taxonomy. Phase 1 establishes the prefixes and seeds the
 * initial codes that Phase 2 will use when wrapping real integrations.
 * Plan ref: `doc/server-error-monitoring-plan.md` §4.3.
 *
 * Conventions:
 * - Domain prefix groups codes by source module, not by user impact.
 * - `001..099` reserved for OAuth/auth/setup failures within the domain.
 * - `100..199` reserved for primary operations (send, generate, refresh).
 * - `900..999` reserved for "unknown / fell through parser".
 *
 * When adding codes, keep them stable — codes appear in PostHog filters and
 * Slack alert routes. Don't renumber once shipped.
 */

export const ERROR_CODES = {
  // ── ACCT_* — Fortnox / PowerOffice OAuth & invoicing ───────────────────
  ACCT_OAUTH_INIT_FAILED: "ACCT_001",
  ACCT_OAUTH_CALLBACK_FAILED: "ACCT_002",
  ACCT_TOKEN_REFRESH_FAILED: "ACCT_010",
  ACCT_TOKEN_REFRESH_PERMANENT: "ACCT_011",
  ACCT_CREDENTIALS_MISSING: "ACCT_012",
  ACCT_CREDENTIALS_DECRYPT_FAILED: "ACCT_013",
  ACCT_API_RATE_LIMITED: "ACCT_020",
  ACCT_INVOICE_CREATE_FAILED: "ACCT_100",
  ACCT_INVOICE_CUSTOMER_SYNC_FAILED: "ACCT_101",
  ACCT_INVOICE_ARTICLE_MAPPING_MISSING: "ACCT_102",
  ACCT_INVOICE_VAT_MISMATCH: "ACCT_103",
  ACCT_UNKNOWN: "ACCT_900",

  // ── EMAIL_* — Postmark per-send & template render ──────────────────────
  EMAIL_SEND_FAILED: "EMAIL_001",
  EMAIL_INVALID_RECIPIENT: "EMAIL_002",
  EMAIL_BOUNCED: "EMAIL_003",
  EMAIL_SENDER_SIGNATURE_REJECTED: "EMAIL_004",
  EMAIL_TEMPLATE_NOT_FOUND: "EMAIL_010",
  EMAIL_TEMPLATE_RENDER_FAILED: "EMAIL_011",
  EMAIL_TEMPLATE_VARIABLE_MISSING: "EMAIL_012",
  EMAIL_BCC_PARSE_FAILED: "EMAIL_020",
  EMAIL_RATE_LIMITED: "EMAIL_030",
  EMAIL_UNKNOWN: "EMAIL_900",

  // ── BOOK_* — Booking creation / transitions ────────────────────────────
  BOOK_CREATE_FAILED: "BOOK_001",
  BOOK_DOUBLE_BOOK_CONFLICT: "BOOK_002",
  BOOK_STATUS_TRANSITION_INVALID: "BOOK_010",
  BOOK_PRICING_FAILED: "BOOK_020",
  BOOK_DURATION_MISSING: "BOOK_030",
  BOOK_UNKNOWN: "BOOK_900",

  // ── RLS_* — Postgres 42501 cross-tenant attempts ───────────────────────
  RLS_ACCESS_DENIED: "RLS_001",

  // ── DB_* — other Postgres failures ─────────────────────────────────────
  DB_UNIQUE_VIOLATION: "DB_001", // PG 23505
  DB_FK_VIOLATION: "DB_002", // PG 23503
  DB_NOT_NULL_VIOLATION: "DB_003", // PG 23502
  DB_NO_ROWS: "DB_010", // PostgREST PGRST116 single() with 0 rows
  DB_MULTIPLE_ROWS: "DB_011", // PostgREST PGRST116 single() with >1 rows
  DB_TIMEOUT: "DB_020",
  DB_UNKNOWN: "DB_900",

  // ── AUTH_* — auth boundary failures (not "no session" — that's silent) ─
  AUTH_SESSION_INVALID: "AUTH_001",
  AUTH_ROLE_RESOLUTION_FAILED: "AUTH_010",
  AUTH_VENUE_ACCESS_DENIED: "AUTH_020",

  // ── SYS_* — uncaught / unknown ─────────────────────────────────────────
  SYS_UNCAUGHT_EXCEPTION: "SYS_001",
  SYS_UNHANDLED_REJECTION: "SYS_002",
  SYS_NODE_WARNING: "SYS_003",
  SYS_UNKNOWN: "SYS_900",

  // ── JOB_* — Trigger.dev job internals (not the task wrapper) ───────────
  // Trigger.dev's Slack integration covers task-level failure. These codes
  // cover the gap inside a task: per-iteration loop failures that the task
  // swallows + continues, and fire-and-forget `.trigger()` calls whose
  // failure doesn't fail the parent task.
  JOB_ITERATION_FAILED: "JOB_001",
  JOB_FOLLOWUP_TRIGGER_FAILED: "JOB_002",

  // ── API_* — caught by the API middleware outer catch (Phase 3) ─────────
  API_UNCAUGHT: "API_001",

  // ── SVRACT_* — caught by the server-action wrapper (Phase 3) ───────────
  SERVER_ACTION_UNCAUGHT: "SVRACT_001",

  // ── REACT_* — React error boundaries (Phase 4) ─────────────────────────
  REACT_BOUNDARY_FAILED: "REACT_001", // app/error.tsx
  SSR_RENDER_FAILED: "REACT_002", // app/global-error.tsx (root SSR failure)
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];
